import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { StatusBadge } from "@/components/ui/status-badge"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Bell, Check, X, Eye } from "lucide-react"
import { cn } from "@/lib/utils"

interface Notification {
  id: string
  title: string
  description: string
  type: "info" | "success" | "warning" | "error"
  time: string
  read: boolean
  actionUrl?: string
}

interface NotificationCenterProps {
  notifications?: Notification[]
  unreadCount?: number
}

const mockNotifications: Notification[] = [
  {
    id: "1",
    title: "模板审核通过",
    description: "您的验证码模板已审核通过，可以正常使用",
    type: "success",
    time: "2024-01-15 14:30",
    read: false,
    actionUrl: "/templates"
  },
  {
    id: "2", 
    title: "签名审核中",
    description: "您的企业签名正在审核中，预计1-2个工作日完成",
    type: "info",
    time: "2024-01-15 10:20",
    read: false,
    actionUrl: "/signatures"
  },
  {
    id: "3",
    title: "余额不足提醒",
    description: "账户余额低于1000元，请及时充值以免影响使用",
    type: "warning", 
    time: "2024-01-14 16:45",
    read: true,
    actionUrl: "/account"
  }
]

export function NotificationCenter({ 
  notifications = mockNotifications,
  unreadCount = 2 
}: NotificationCenterProps) {
  const [open, setOpen] = useState(false)
  const [localNotifications, setLocalNotifications] = useState(notifications)

  const markAsRead = (id: string) => {
    setLocalNotifications(prev => 
      prev.map(notif => 
        notif.id === id ? { ...notif, read: true } : notif
      )
    )
  }

  const markAllAsRead = () => {
    setLocalNotifications(prev => 
      prev.map(notif => ({ ...notif, read: true }))
    )
  }

  const removeNotification = (id: string) => {
    setLocalNotifications(prev => 
      prev.filter(notif => notif.id !== id)
    )
  }

  const getStatusFromType = (type: string) => {
    const typeMap: Record<string, any> = {
      'success': 'success',
      'info': 'pending',
      'warning': 'warning',
      'error': 'failed'
    }
    return typeMap[type] || 'pending'
  }

  const currentUnreadCount = localNotifications.filter(n => !n.read).length

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative">
          <Bell className="h-5 w-5" />
          {currentUnreadCount > 0 && (
            <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 text-xs bg-red-500 hover:bg-red-500">
              {currentUnreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <Card className="border-0 shadow-none">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">通知中心</CardTitle>
              {currentUnreadCount > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={markAllAsRead}
                  className="text-xs"
                >
                  全部已读
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-80">
              {localNotifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Bell className="h-8 w-8 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500">暂无通知</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {localNotifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={cn(
                        "flex items-start space-x-3 p-3 hover:bg-gray-50 transition-colors border-l-2",
                        notification.read 
                          ? "border-l-transparent bg-gray-50/50" 
                          : "border-l-blue-500 bg-white"
                      )}
                    >
                      <div className="flex-shrink-0 mt-1">
                        <StatusBadge 
                          status={getStatusFromType(notification.type)}
                          size="sm"
                          showIcon={true}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className={cn(
                              "text-sm font-medium",
                              notification.read ? "text-gray-600" : "text-gray-900"
                            )}>
                              {notification.title}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {notification.description}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              {notification.time}
                            </p>
                          </div>
                          <div className="flex items-center space-x-1 ml-2">
                            {!notification.read && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => markAsRead(notification.id)}
                                className="h-6 w-6 p-0"
                              >
                                <Check className="h-3 w-3" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeNotification(notification.id)}
                              className="h-6 w-6 p-0"
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
            {localNotifications.length > 0 && (
              <div className="border-t p-3">
                <Button variant="ghost" size="sm" className="w-full">
                  <Eye className="h-4 w-4 mr-2" />
                  查看全部通知
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </PopoverContent>
    </Popover>
  )
}